import { getFaceImage } from './api';

export const normalizeFaceIds = (faceIds) => (
  (Array.isArray(faceIds) ? faceIds : [])
    .filter(Boolean)
    .map((id) => String(id).trim())
    .filter(Boolean)
);

const createEnrolledFaceImageLoader = () => {
  const cache = new Map();
  const inFlight = new Map();

  const loadFaceImageUrl = async (faceId) => {
    const id = String(faceId || '').trim();
    if (!id) return null;
    if (cache.has(id)) return cache.get(id);
    let pending = inFlight.get(id);
    if (!pending) {
      pending = getFaceImage(id)
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          cache.set(id, url);
          return url;
        })
        .catch(() => null)
        .finally(() => {
          inFlight.delete(id);
        });
      inFlight.set(id, pending);
    }
    return pending;
  };

  return { loadFaceImageUrl };
};

/** Open full-screen gallery for all enrolled faces (lazy-loads beyond thumbnail). */
export const openEnrolledFaceGallery = (openImagePreview, {
  faceIds,
  thumbnailSrc = '',
  employeeName = '',
  employeeId = '',
  hideTitle = true,
} = {}) => {
  if (typeof openImagePreview !== 'function') return;

  const ids = normalizeFaceIds(faceIds);
  const thumb = thumbnailSrc || '';

  if (!ids.length) {
    if (!thumb) return;
    openImagePreview({
      src: thumb,
      title: employeeName || 'Profile image',
      alt: employeeName || 'Profile image',
      employeeName,
      employeeId,
      employeeImage: thumb,
      hideTitle,
    });
    return;
  }

  const galleryImages = ids.map((faceId, index) => ({
    src: index === 0 && thumb ? thumb : '',
    title: `Face ${index + 1}`,
    alt: `Face ${index + 1}`,
    faceId,
  }));

  const { loadFaceImageUrl } = createEnrolledFaceImageLoader();
  const initial = galleryImages[0];

  openImagePreview({
    src: initial.src || thumb || '',
    title: initial.title,
    alt: initial.alt,
    images: galleryImages,
    index: 0,
    employeeName,
    employeeId,
    employeeImage: thumb || initial.src || '',
    hideTitle,
    resolveImageSrc: async (item, index) => {
      const existing = item?.src || galleryImages[index]?.src;
      if (existing) return existing;
      const faceId = item?.faceId || galleryImages[index]?.faceId;
      return loadFaceImageUrl(faceId);
    },
  });
};
